// Fonctions utilitaires du moteur de rapports : filtres de dates, libellés des
// filtres appliqués et préparation des lignes/colonnes pour l'export.

import { formatDate } from "@/utils/format";
import type { ExportColumn } from "@/utils/export";
import type {
  ReportFilters,
  ReportOption,
  ReportOptions,
  ReportResult,
  ReportRow,
  StatutFilterConfig,
} from "./types";

export const EMPTY_FILTERS: ReportFilters = {
  dateFrom: "",
  dateTo: "",
  immeubleId: "",
  bienId: "",
  locataireId: "",
  proprietaireId: "",
  statut: "",
};

/** Vrai si une date ISO (`YYYY-MM-DD…`) est comprise dans l'intervalle fourni. */
export function inDateRange(value: string, from: string, to: string): boolean {
  if (!value) return !from && !to;
  const day = value.slice(0, 10);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

/** Vrai si une période (`YYYY-MM`) est comprise dans l'intervalle fourni. */
export function inPeriodRange(periode: string, from: string, to: string): boolean {
  if (!periode) return !from && !to;
  const fromKey = from ? from.slice(0, 7) : "";
  const toKey = to ? to.slice(0, 7) : "";
  if (fromKey && periode < fromKey) return false;
  if (toKey && periode > toKey) return false;
  return true;
}

/** Convertit une date ISO en clé de période `YYYY-MM`. */
export function periodKeyOf(iso: string): string {
  return iso ? iso.slice(0, 7) : "";
}

/** Libellé lisible de la période sélectionnée. */
export function periodLabel(filters: ReportFilters): string {
  const { dateFrom, dateTo } = filters;
  if (!dateFrom && !dateTo) return "Toute la période";
  if (dateFrom && dateTo) return `Du ${formatDate(dateFrom)} au ${formatDate(dateTo)}`;
  if (dateFrom) return `À partir du ${formatDate(dateFrom)}`;
  return `Jusqu'au ${formatDate(dateTo)}`;
}

function labelOf(items: ReportOption[] | undefined, value: string): string {
  return items?.find((item) => item.value === value)?.label ?? value;
}

/** Liste des filtres réellement appliqués, avec leur libellé lisible. */
export function appliedFilterLines(
  filters: ReportFilters,
  options: ReportOptions,
  statutConfig: StatutFilterConfig
): { label: string; value: string }[] {
  const lines: { label: string; value: string }[] = [{ label: "Période", value: periodLabel(filters) }];
  if (filters.proprietaireId) {
    lines.push({ label: "Propriétaire", value: labelOf(options.proprietaires, filters.proprietaireId) });
  }
  if (filters.immeubleId) {
    lines.push({ label: "Immeuble", value: labelOf(options.immeubles, filters.immeubleId) });
  }
  if (filters.bienId) {
    lines.push({ label: "Bien", value: labelOf(options.biens, filters.bienId) });
  }
  if (filters.locataireId) {
    lines.push({ label: "Locataire", value: labelOf(options.locataires, filters.locataireId) });
  }
  if (filters.statut) {
    lines.push({ label: statutConfig.label, value: labelOf(statutConfig.options, filters.statut) });
  }
  return lines;
}

/** Colonnes d'export dérivées des colonnes du rapport. */
export function buildExportColumns(result: ReportResult): ExportColumn<ReportRow>[] {
  return result.columns.map((column) => ({
    header: column.header,
    value: (row) => row[column.key] ?? "",
  }));
}

/**
 * Lignes d'export : les lignes du rapport suivies des totaux.
 * Chaque total place son libellé dans la première colonne et sa valeur dans la
 * dernière, afin que le fichier exporté reflète exactement l'aperçu affiché.
 */
export function buildExportRows(result: ReportResult): ReportRow[] {
  if (result.columns.length === 0 || result.totals.length === 0) return result.rows;
  const firstKey = result.columns[0].key;
  const lastKey = result.columns[result.columns.length - 1].key;
  const totalRows: ReportRow[] = result.totals.map((total) => {
    const row: ReportRow = {};
    result.columns.forEach((column) => {
      row[column.key] = "";
    });
    row[firstKey] = total.label;
    row[lastKey] = total.value;
    return row;
  });
  return [...result.rows, ...totalRows];
}

/** Construit le nom de fichier d'export d'un rapport. */
export function reportFilename(prefix: string, typeId: string): string {
  return `${prefix}-${typeId}`;
}